/**
 * Confidence Gauge
 *
 * Circular gauge showing profile maturity/confidence level.
 */

import { motion } from 'framer-motion'

interface ConfidenceGaugeProps {
  value: number // 0-1
  size?: 'sm' | 'md' | 'lg'
}

export default function ConfidenceGauge({ value, size = 'md' }: ConfidenceGaugeProps) {
  const sizeConfig = {
    sm: { width: 100, stroke: 8, fontSize: 'text-lg' },
    md: { width: 160, stroke: 12, fontSize: 'text-2xl' },
    lg: { width: 200, stroke: 14, fontSize: 'text-3xl' },
  }

  const config = sizeConfig[size]
  const radius = (config.width - config.stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (value * circumference)

  const getColorForValue = (val: number) => {
    if (val >= 0.7) return '#22c55e' // Green - Mature
    if (val >= 0.4) return '#3b82f6' // Blue - Established
    if (val >= 0.2) return '#eab308' // Yellow - Emerging
    return '#94a3b8' // Gray - Minimal
  }

  const getLabelForValue = (val: number) => {
    if (val >= 0.7) return 'Mature'
    if (val >= 0.4) return 'Established'
    if (val >= 0.2) return 'Emerging'
    return 'Minimal'
  }

  const color = getColorForValue(value)
  const label = getLabelForValue(value)
  const percentage = Math.round(value * 100)

  return (
    <div className="relative" style={{ width: config.width, height: config.width }}>
      {/* Background circle */}
      <svg
        width={config.width}
        height={config.width}
        className="transform -rotate-90"
      >
        <circle
          cx={config.width / 2}
          cy={config.width / 2}
          r={radius}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth={config.stroke}
        />
        <motion.circle
          cx={config.width / 2}
          cy={config.width / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={config.stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className={`font-bold ${config.fontSize}`}
          style={{ color }}
        >
          {percentage}%
        </motion.span>
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-xs text-gray-500 uppercase tracking-wide"
        >
          {label}
        </motion.span>
      </div>
    </div>
  )
}
